import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import TableItem from './TableItem';

import Button from '../../../common/Button';
import Pagination from '../../../common/Pagination';

import { fetchGetProductsHistory, fetchProductsHistoryDiff } from '../../../../actions/items';

import { parseError } from '../../../../helpers';

import { PRODUCT_CHANGES_PER_PAGE_SELECT_OPTIONS } from '../../../../config';
import { useAutoTranslation } from '../../../../helpers/useAutoTranslation';

const Wrapper = styled('div')`
  position: relative;
  padding: 1.5rem 0;
  > div:not(.border-block) {
    margin-left: calc(1.5rem - 12px);
    margin-right: calc(1.5rem - 12px);
  }
  .border-block {
    border-top: 1px solid #f7f6f6;
  }

  .fixed-table-body {
    overflow-x: auto;
  }

  .badge {
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: #737373;
    padding: 4px 8px;
    border-radius: 4px;
    &.badge-blue {
      background: #d0eeff;
    }
    &.badge-grey {
      background: #e8e8e8;
    }
  }

  table.table {
    border-bottom: 1px solid #dee2e6;
    .td-btn {
      width: 120px;
      button {
        width: 120px;
        display: flex;
        justify-content: center;
      }
    }
    .td-date {
      width: 150px;
      .light {
        font-weight: 400;
        font-size: 12px;
        line-height: 20px;
        color: #98a6ad;
        margin-top: 2px;
      }
    }
    .td-user {
      width: 210px;
    }
    .td-source {
      width: 140px;
    }
    .even {
      background: #fafafa;
    }
    .td-btn {
      button {
        font-size: 14px;
        display: flex;
        align-items: center;
        margin-right: -10px;
      }
      i {
        font-size: 14px;
      }
    }
    .td-btn span {
      display: block;
    }
    th {
      font-weight: 500;
      font-size: 14px;
    }
    tr {
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      color: #323a46;
      &.more-row {
        td {
          border-top: none;
        }
        h5 {
          font-weight: 500;
          font-size: 14px;
          line-height: 20px;
          margin-bottom: 7px;
          margin-top: 0;
        }
        p {
          margin-bottom: 0;
          color: #6e768e;
          line-height: 24px;
        }
      }
    }
    td {
      vertical-align: middle;
      color: #343a40;
    }
  }

  .header-title {
    font-weight: 500;
    font-size: 16px;
    line-height: 22px;
    color: #323a46;
  }
`;

const ProductChangesBlock = (props) => {
  const { productId, loading } = props;

  const [productHistory, setProductHistory] = useState([]);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [loadingFetchDecryption, setLoadingFetchDecryption] = useState(null);
  const [loadingFetchAllDecryption, setLoadingFetchAllDecryption] = useState(false);
  const [lastItemId, setLastItemId] = useState(null);
  const [showRowArr, setShowRowArr] = useState([]);
  const [onMount, setOnMount] = useState(false);

  const dispatch = useDispatch();

  const { t } = useAutoTranslation();

  useEffect(() => {
    (async function () {
      await fetchProductHistory(productId, page, size);
    })();
  }, [productId, page, size]);

  useEffect(() => {
    if (loading) return;

    if (!onMount) {
      setOnMount(true);
      return;
    }
    setTimeout(async () => {
      await fetchProductHistory(productId, page, size);
    }, 1500);
  }, [loading]);

  const fetchProductHistory = async (productId, page, size) => {
    try {
      const productHistory = await dispatch(fetchGetProductsHistory({ productId, page, size }));
      if (!productHistory.events) throw new Error('Ошибка получения списка изменений');

      setProductHistory(productHistory.events);

      const qtyOfRecordsToRender = productHistory.recordsTotal - 1;
      setTotalItems(qtyOfRecordsToRender);

      if (productHistory.events.length) {
        getLastItem(qtyOfRecordsToRender, productHistory);
      }
    } catch (e) {
      toast.error(parseError(e));
    }
  };

  const getLastItem = (qtyOfRecordsToRender, productHistory) => {
    const pagesCount =
      qtyOfRecordsToRender % size
        ? Math.trunc(qtyOfRecordsToRender / size) + 1
        : qtyOfRecordsToRender / size;
    const lastItem = productHistory.events[productHistory.events.length - 1];
    if (pagesCount === page + 1) {
      setLastItemId(lastItem.id);
    }
  };

  const getNeighboringEvents = async (id, isLast) => {
    const pagesCount = totalItems % size ? Math.trunc(totalItems / size) + 1 : totalItems / size;
    if (isLast && page + 1 < pagesCount) {
      const firstEventOnNextPage = await getFirstEventOnNextPage();
      return [firstEventOnNextPage.id, id];
    }

    const findProductIndex = productHistory.findIndex((event) => event.id === id);
    const prevProduct =
      findProductIndex < productHistory.length - 1
        ? productHistory[findProductIndex + 1]
        : productHistory[productHistory.length - 1];

    return [prevProduct.id, id];
  };

  const getFetchAllDecryptionReqData = async () => {
    return await Promise.all(
      productHistory.map(async (event, i) => {
        const [leftId, rightId] = await getNeighboringEvents(
          event.id,
          i === productHistory.length - 1,
        );
        return {
          leftId,
          rightId,
        };
      }),
    );
  };

  const handleFetchDecryption = async (id) => {
    setLoadingFetchDecryption(id);

    const index = productHistory.findIndex((event) => event.id === id);
    const [leftId, rightId] = await getNeighboringEvents(id, index === productHistory.length - 1);

    const reqDateObj = { leftId, rightId };
    try {
      setLoadingFetchDecryption(id);
      const productDiff = await dispatch(fetchProductsHistoryDiff({ items: [reqDateObj] }));

      const diff = productDiff.items.find((event) => event.rightId === id);
      const newProductHistory = productHistory.map((event) => {
        return event.id === id
          ? {
              ...event,
              diff: diff.diff,
            }
          : event;
      });
      setProductHistory(newProductHistory);
      setShowRowArr((state) => [...state, id]);
    } catch (e) {
      toast.error(parseError(e));
    } finally {
      setLoadingFetchDecryption(null);
    }
  };

  const getFirstEventOnNextPage = async () => {
    try {
      const productHistory = await dispatch(
        fetchGetProductsHistory({ productId, page: page + 1, size }),
      );
      if (!productHistory.events) throw new Error('Ошибка получения списка изменений');

      return productHistory.events[0];
    } catch (e) {
      toast.error(parseError(e));
    }
  };

  const handleFetchAllDecryption = async () => {
    setLoadingFetchAllDecryption(true);

    const items = await getFetchAllDecryptionReqData();
    try {
      const { items: productDiff } = await dispatch(fetchProductsHistoryDiff({ items }));

      const newProductHistory = productHistory.map((event) => {
        const diff = productDiff.find((d) => d.rightId === event.id);
        return diff
          ? {
              ...event,
              diff: diff.diff,
            }
          : event;
      });
      setProductHistory(newProductHistory);
      setShowRowArr(newProductHistory.map((p) => p.id));
    } catch (e) {
      toast.error(parseError(e));
    } finally {
      setLoadingFetchAllDecryption(false);
    }
  };

  return (
    <Wrapper className="card-box" style={{ height: 'auto' }}>
      <div className="row mb-1">
        <div className="col-12 d-flex justify-content-between align-items-sm-center flex-sm-row flex-column">
          <h4 className="header-title">{t('История изменений товара')}</h4>
          {productHistory.length > 1 ? (
            <Button
              type="button"
              text={t('Показать все')}
              classType="btn-secondary"
              className="xs-ml-2 mb-3"
              icon={'fe-chevron-down'}
              onClick={handleFetchAllDecryption}
              loading={loadingFetchAllDecryption}
              disabled={loadingFetchAllDecryption}
            />
          ) : null}
        </div>
      </div>
      {productHistory.length ? (
        <div className="row">
          <div className="col-12">
            <div className="bootstrap-table">
              <div className="fixed-table-container">
                <div className="fixed-table-body">
                  <table className="table table-centered">
                    <thead className="thead-light">
                      <tr>
                        <th>{t('Дата изменения')}</th>
                        <th>{t('Логин пользователя')}</th>
                        <th>{t('Источник')}</th>
                        <th>{t('Описание')}</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {productHistory.map((item, itemIndex) => (
                        <TableItem
                          key={`${item.id}`}
                          item={item}
                          itemIndex={itemIndex}
                          handleFetchDecryption={() => handleFetchDecryption(item.id)}
                          loading={loadingFetchDecryption}
                          isLastItem={item.id === lastItemId}
                          even={itemIndex % 2 !== 0}
                          showRowArr={showRowArr}
                          setShowRowArr={setShowRowArr}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <Pagination
              withPerPageCountSelect
              perPageSelectOptions={PRODUCT_CHANGES_PER_PAGE_SELECT_OPTIONS}
              total={totalItems}
              currentPage={page}
              perPage={size}
              onPageSelect={(pageIndex) => setPage(pageIndex)}
              onPerPageSelect={(perPageValue) => setSize(perPageValue)}
            />
          </div>
        </div>
      ) : (
        <div className="row">
          <div className="col-12">
            <p>{t('Нет изменений')}</p>
          </div>
        </div>
      )}
    </Wrapper>
  );
};

export default ProductChangesBlock;
